/* filepath: packages/volto-castle-theme/src/config/voltoFormPlaceholders.ts */

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const normalize = (v: string) => (v || '').toString().trim().toLowerCase();

function placeholderForLabel(labelText: string): string | undefined {
  const l = normalize(labelText);
  if (l.includes('name')) return 'Your name';
  if (l.includes('email') || l.includes('e-mail')) return 'you@company.com';
  if (l.includes('subject')) return "What’s this about?";
  if (l.includes('message')) return 'How can we help?';
  return undefined;
}

function isEmailLabel(labelText: string): boolean {
  const l = normalize(labelText);
  return l.includes('email') || l.includes('e-mail');
}

function dispatchValueEvents(el: HTMLInputElement | HTMLTextAreaElement) {
  // Helps React/Semantic UI notice the change
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function getEmailFromSearch(search: string): string | undefined {
  if (!search) return undefined;
  const params = new URLSearchParams(search);
  const email = params.get('email')?.trim();
  return email || undefined;
}

/**
 * Remove `email` from the URL querystring without navigation.
 * Keeps any other params intact.
 */
function removeEmailParamFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('email')) return;

  url.searchParams.delete('email');

  // Preserve hash
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

/**
 * Applies placeholders and (optionally) prefills email from querystring.
 * Returns true if it successfully prefills at least one email field.
 */
function applyPlaceholdersAndPrefillEmail(emailFromQuery?: string, root: ParentNode = document): boolean {
  let didPrefillEmail = false;

  const blocks = root.querySelectorAll<HTMLElement>('.block.form');

  blocks.forEach((block) => {
    const fields = block.querySelectorAll<HTMLElement>('.field');

    fields.forEach((field) => {
      const labelEl = field.querySelector<HTMLLabelElement>('label');
      const inputEl = field.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        'input:not([type="hidden"]), textarea',
      );

      if (!labelEl || !inputEl) return;

      // 1) Placeholder (only if missing)
      if (!inputEl.getAttribute('placeholder')) {
        const ph = placeholderForLabel(labelEl.textContent || '');
        if (ph) inputEl.setAttribute('placeholder', ph);
      }

      // 2) Prefill email (only if query provides + looks like email field + empty)
      if (emailFromQuery) {
        const labelText = labelEl.textContent || '';
        const looksLikeEmailField =
          isEmailLabel(labelText) ||
          (inputEl instanceof HTMLInputElement && inputEl.type === 'email') ||
          normalize(inputEl.name || '').includes('email');

        if (looksLikeEmailField) {
          const current = (inputEl as HTMLInputElement).value ?? '';
          if (!current) {
            (inputEl as HTMLInputElement).value = emailFromQuery;
            dispatchValueEvents(inputEl);
            didPrefillEmail = true;
          }
        }
      }
    });
  });

  return didPrefillEmail;
}

export default function VoltoFormPlaceholders(): JSX.Element | null {
  const location = useLocation();

  // Make sure we only "clean" the URL once per navigation that contains ?email=
  const cleanedForLocationKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const emailFromQuery = getEmailFromSearch(location.search);

    const run = () => {
      const didPrefill = applyPlaceholdersAndPrefillEmail(emailFromQuery);

      // If we successfully populated the email field, remove the query param once.
      if (
        emailFromQuery &&
        didPrefill &&
        cleanedForLocationKeyRef.current !== location.key
      ) {
        cleanedForLocationKeyRef.current = location.key;
        removeEmailParamFromUrl();
      }
    };

    // Run immediately on mount + route change
    run();

    // Keep applying as Volto renders/updates blocks async
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList') {
          run();
          break;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => observer.disconnect();
  }, [location.key, location.search]);

  return null;
}